import { useState } from 'react';
import { useAuthStore } from '@/store/authStore';

interface UserRole {
  id: string;
  name: string;
  displayName: string;
  description: string;
  permissions: string[];
  color: string;
}

const USER_ROLES: UserRole[] = [
  {
    id: 'free_user',
    name: 'Free User',
    displayName: 'Free',
    description: 'Basic access to public data',
    permissions: [
      'view_public_elections',
      'view_candidates',
      'view_offices',
      'basic_search',
    ],
    color: 'bg-gray-600',
  },
  {
    id: 'premium_user',
    name: 'Premium User',
    displayName: 'Premium',
    description: 'Full access to all data and features',
    permissions: [
      'view_public_elections',
      'view_candidates',
      'view_offices',
      'basic_search',
      'view_polling_data',
      'view_market_odds',
      'advanced_search',
      'watchlist',
      'alerts',
      'export_data',
      'api_access',
    ],
    color: 'bg-purple-600',
  },
  {
    id: 'editor',
    name: 'Editor',
    displayName: 'Editor',
    description: 'Can create and edit content',
    permissions: [
      'view_public_elections',
      'view_candidates',
      'view_offices',
      'basic_search',
      'view_polling_data',
      'view_market_odds',
      'advanced_search',
      'watchlist',
      'alerts',
      'export_data',
      'api_access',
      'create_discussions',
      'edit_discussions',
      'moderate_comments',
    ],
    color: 'bg-blue-600',
  },
  {
    id: 'admin',
    name: 'Administrator',
    displayName: 'Admin',
    description: 'Full system access',
    permissions: [
      'view_public_elections',
      'view_candidates',
      'view_offices',
      'basic_search',
      'view_polling_data',
      'view_market_odds',
      'advanced_search',
      'watchlist',
      'alerts',
      'export_data',
      'api_access',
      'create_discussions',
      'edit_discussions',
      'moderate_comments',
      'manage_users',
      'manage_content',
      'view_analytics',
      'system_settings',
      'impersonate_users',
    ],
    color: 'bg-red-600',
  },
];

const IMPERSONATION_STORAGE_KEY = 'republic-atlas-impersonation';

export const UserImpersonation = () => {
  const { user, isAuthenticated } = useAuthStore();
  const [impersonatedRole, setImpersonatedRole] = useState<string | null>(() => {
    return localStorage.getItem(IMPERSONATION_STORAGE_KEY);
  });
  const [, setSelectedRole] = useState<UserRole | null>(null);

  const currentRole = impersonatedRole || user?.role || 'free_user';
  const currentRoleData = USER_ROLES.find(r => r.id === currentRole) || USER_ROLES[0];

  const startImpersonation = (roleId: string) => {
    setImpersonatedRole(roleId);
    localStorage.setItem(IMPERSONATION_STORAGE_KEY, roleId);
    // In a real app, you would also update the auth context/store
  };

  const stopImpersonation = () => {
    setImpersonatedRole(null);
    localStorage.removeItem(IMPERSONATION_STORAGE_KEY);
  };

  const hasPermission = (permission: string) => {
    return currentRoleData.permissions.includes(permission);
  };

  const allPermissions = [
    { id: 'view_public_elections', name: 'View Elections', category: 'Read' },
    { id: 'view_candidates', name: 'View Candidates', category: 'Read' },
    { id: 'view_offices', name: 'View Offices', category: 'Read' },
    { id: 'basic_search', name: 'Basic Search', category: 'Read' },
    { id: 'view_polling_data', name: 'View Polling Data', category: 'Premium' },
    { id: 'view_market_odds', name: 'View Market Odds', category: 'Premium' },
    { id: 'advanced_search', name: 'Advanced Search', category: 'Premium' },
    { id: 'watchlist', name: 'Watchlist', category: 'Premium' },
    { id: 'alerts', name: 'Alerts', category: 'Premium' },
    { id: 'export_data', name: 'Export Data', category: 'Premium' },
    { id: 'api_access', name: 'API Access', category: 'Premium' },
    { id: 'create_discussions', name: 'Create Discussions', category: 'Editor' },
    { id: 'edit_discussions', name: 'Edit Discussions', category: 'Editor' },
    { id: 'moderate_comments', name: 'Moderate Comments', category: 'Editor' },
    { id: 'manage_users', name: 'Manage Users', category: 'Admin' },
    { id: 'manage_content', name: 'Manage Content', category: 'Admin' },
    { id: 'view_analytics', name: 'View Analytics', category: 'Admin' },
    { id: 'system_settings', name: 'System Settings', category: 'Admin' },
    { id: 'impersonate_users', name: 'Impersonate Users', category: 'Admin' },
  ];

  return (
    <div className="space-y-4">
      {/* Natural Language Summary */}
      <div className="bg-gradient-to-r from-pink-900/50 to-rose-900/50 rounded-lg p-3 border border-pink-500/30">
        <p className="text-sm">
          <span className="text-pink-400 font-medium">Role Status:</span>{' '}
          <span className="text-gray-200">
            Viewing as <span className={`font-medium ${currentRoleData.color.replace('bg-', 'text-')}`}>
              {currentRoleData.displayName}
            </span>
            {impersonatedRole && (
              <span className="text-yellow-400"> (impersonated)</span>
            )}
            {' • '}{currentRoleData.permissions.length} permissions active
          </span>
        </p>
      </div>

      {/* Current Status */}
      {!isAuthenticated && (
        <div className="bg-yellow-900/30 border border-yellow-500/50 rounded-lg p-3">
          <p className="text-sm text-yellow-400">
            ⚠️ Not logged in. Impersonation only affects local UI state, not actual API permissions.
          </p>
        </div>
      )}

      {/* Impersonation Warning */}
      {impersonatedRole && (
        <div className="bg-pink-900/30 border border-pink-500/50 rounded-lg p-3 flex items-center justify-between">
          <div>
            <div className="text-sm text-pink-400 font-medium">🎭 Impersonation Active</div>
            <div className="text-xs text-gray-400">
              Viewing UI as {currentRoleData.name}. API calls still use your real role.
            </div>
          </div>
          <button
            onClick={stopImpersonation}
            className="bg-pink-600 hover:bg-pink-700 px-3 py-1 rounded text-xs"
          >
            Stop
          </button>
        </div>
      )}

      {/* Role Selector */}
      <div className="bg-gray-800 rounded-lg p-3">
        <h4 className="text-sm font-medium text-pink-400 mb-2">🎭 Switch Role</h4>
        <div className="grid grid-cols-2 gap-2">
          {USER_ROLES.map((role) => (
            <button
              key={role.id}
              onClick={() => {
                setSelectedRole(role);
                startImpersonation(role.id);
              }}
              className={`p-3 rounded-lg text-left transition-all ${
                currentRole === role.id
                  ? `${role.color} ring-2 ring-white/50`
                  : 'bg-gray-900 hover:bg-gray-850'
              }`}
            >
              <div className="flex items-center gap-2">
                <span className={`w-3 h-3 rounded-full ${role.color}`} />
                <span className="font-medium text-sm">{role.displayName}</span>
              </div>
              <p className="text-[10px] text-gray-400 mt-1">{role.description}</p>
              <p className="text-[10px] text-gray-600 mt-1">{role.permissions.length} permissions</p>
            </button>
          ))}
        </div>
      </div>

      {/* Permission Matrix */}
      <div className="bg-gray-800 rounded-lg p-3">
        <h4 className="text-sm font-medium text-pink-400 mb-2">🔐 Permission Matrix</h4>
        <div className="overflow-x-auto">
          <table className="w-full text-[10px]">
            <thead>
              <tr className="border-b border-gray-700">
                <th className="text-left p-1 text-gray-500">Permission</th>
                {USER_ROLES.map(role => (
                  <th key={role.id} className="text-center p-1 text-gray-500 w-16">
                    {role.displayName}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {allPermissions.map((perm) => (
                <tr key={perm.id} className="border-b border-gray-800">
                  <td className="p-1">
                    <span className="text-gray-300">{perm.name}</span>
                    <span className="text-gray-600 ml-1">({perm.category})</span>
                  </td>
                  {USER_ROLES.map(role => (
                    <td key={role.id} className="text-center p-1">
                      {role.permissions.includes(perm.id) ? (
                        <span className="text-green-400">✓</span>
                      ) : (
                        <span className="text-gray-600">—</span>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Current User Info */}
      {isAuthenticated && (
        <div className="bg-gray-800 rounded-lg p-3">
          <h4 className="text-sm font-medium text-pink-400 mb-2">👤 Actual User</h4>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="bg-gray-900 rounded p-2">
              <span className="text-gray-500">Email:</span>
              <span className="ml-2 text-gray-300">{user?.email}</span>
            </div>
            <div className="bg-gray-900 rounded p-2">
              <span className="text-gray-500">Real Role:</span>
              <span className="ml-2 text-gray-300">{user?.role || 'free_user'}</span>
            </div>
            <div className="bg-gray-900 rounded p-2">
              <span className="text-gray-500">User ID:</span>
              <span className="ml-2 text-gray-300 font-mono text-[10px]">{user?.id}</span>
            </div>
            <div className="bg-gray-900 rounded p-2">
              <span className="text-gray-500">Subscription:</span>
              <span className="ml-2 text-gray-300">{user?.subscriptionStatus || 'none'}</span>
            </div>
          </div>
        </div>
      )}

      {/* Permission Check Tool */}
      <div className="bg-gray-800 rounded-lg p-3">
        <h4 className="text-sm font-medium text-pink-400 mb-2">🔍 Permission Check</h4>
        <div className="flex flex-wrap gap-1">
          {['view_polling_data', 'watchlist', 'manage_users', 'api_access'].map(perm => (
            <span
              key={perm}
              className={`px-2 py-1 rounded text-[10px] ${
                hasPermission(perm)
                  ? 'bg-green-600/30 text-green-400'
                  : 'bg-red-600/30 text-red-400'
              }`}
            >
              {perm}: {hasPermission(perm) ? '✓' : '✗'}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

// Hook for checking permissions
export const useHasPermission = (permission: string): boolean => {
  const { user } = useAuthStore();
  const impersonatedRole = localStorage.getItem(IMPERSONATION_STORAGE_KEY);
  const currentRole = impersonatedRole || user?.role || 'free_user';
  const roleData = USER_ROLES.find(r => r.id === currentRole);
  return roleData?.permissions.includes(permission) || false;
};

